import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Search,
  Pencil,
  Trash2,
  Save,
  X,
  AlertCircle,
  ImageOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AdminTabs } from './AdminTabs';

const COLLECTIONS = ['books', 'apparel', 'originals', 'studio'];

const emptyDraft = () => ({
  id: '',
  slug: '',
  title: '',
  description: '',
  price: '',
  priceLabel: '',
  paymentUrl: '',
  image: '',
  imagesJson: '',
  badge: '',
  collection: 'studio',
  originalCategory: '',
  sold: false,
});

const productToDraft = (p) => ({
  id: p.id || '',
  slug: p.slug || '',
  title: p.title || '',
  description: p.description || '',
  price: p.price === null || p.price === undefined ? '' : String(p.price),
  priceLabel: p.priceLabel || '',
  paymentUrl: p.paymentUrl || '',
  image: p.image || '',
  imagesJson: p.images && p.images.length ? JSON.stringify(p.images, null, 2) : '',
  badge: p.badge || '',
  collection: p.collection || 'studio',
  originalCategory: p.originalCategory || '',
  sold: !!p.sold,
});

const draftToPayload = (draft, { forCreate }) => {
  const payload = {
    title: draft.title.trim(),
    description: draft.description.trim(),
    image: draft.image.trim(),
    collection: draft.collection,
  };
  if (forCreate && draft.id.trim()) payload.id = draft.id.trim();
  if (draft.slug.trim()) payload.slug = draft.slug.trim();
  if (draft.price !== '' && draft.price !== null && !Number.isNaN(Number(draft.price))) {
    payload.price = Number(draft.price);
  } else if (!forCreate) {
    payload.price = null;
  }
  if (draft.priceLabel.trim()) payload.priceLabel = draft.priceLabel.trim();
  if (draft.paymentUrl.trim()) payload.paymentUrl = draft.paymentUrl.trim();
  if (draft.badge.trim()) payload.badge = draft.badge.trim();
  if (draft.originalCategory.trim()) payload.originalCategory = draft.originalCategory.trim();
  payload.sold = !!draft.sold;

  if (draft.imagesJson.trim()) {
    const parsed = JSON.parse(draft.imagesJson);
    if (!Array.isArray(parsed)) throw new Error('Images must be a JSON array');
    parsed.forEach((img, idx) => {
      if (!img.id || !img.label || !img.url) {
        throw new Error(`Image #${idx + 1} needs id, label, and url`);
      }
    });
    payload.images = parsed;
  } else if (!forCreate) {
    payload.images = null;
  }
  return payload;
};

export const AdminProductsPage = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [drawerMode, setDrawerMode] = useState(null); // 'create' | 'edit' | null
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/products`);
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      data.sort((a, b) => a.title.localeCompare(b.title));
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (collectionFilter !== 'all' && p.collection !== collectionFilter) return false;
      if (q && !`${p.title} ${p.slug}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, collectionFilter, query]);

  const counts = useMemo(() => {
    const c = { all: products.length };
    COLLECTIONS.forEach((k) => (c[k] = products.filter((p) => p.collection === k).length));
    return c;
  }, [products]);

  const openCreate = () => {
    setDraft(emptyDraft());
    setEditingId(null);
    setFormError(null);
    setDrawerMode('create');
  };

  const openEdit = (product) => {
    setDraft(productToDraft(product));
    setEditingId(product.id);
    setFormError(null);
    setDrawerMode('edit');
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setEditingId(null);
    setDraft(emptyDraft());
    setFormError(null);
  };

  const handleSave = async () => {
    setFormError(null);
    let payload;
    try {
      payload = draftToPayload(draft, { forCreate: drawerMode === 'create' });
    } catch (err) {
      setFormError(err.message);
      return;
    }
    if (!payload.title || !payload.description || !payload.image) {
      setFormError('Title, description, and image URL are required.');
      return;
    }
    setSaving(true);
    try {
      const url =
        drawerMode === 'create'
          ? `${backendUrl}/api/products`
          : `${backendUrl}/api/products/${editingId}`;
      const method = drawerMode === 'create' ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed (${res.status})`);
      }
      await fetchProducts();
      closeDrawer();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/products/${product.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete');
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="min-h-screen bg-background" data-testid="admin-products-page">
      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="touch-target rounded-full hover:bg-secondary transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-display text-foreground">Product Catalog</h1>
              <p className="text-xs text-muted-foreground">
                {products.length} pieces · Add, edit, retire
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchProducts}
              disabled={loading}
              data-testid="admin-products-refresh"
            >
              <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="admin-products-new">
              <Plus className="w-4 h-4 mr-2" />
              New product
            </Button>
          </div>
        </div>
      </div>

      <AdminTabs />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filter chips + search */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', ...COLLECTIONS].map((c) => (
              <button
                key={c}
                onClick={() => setCollectionFilter(c)}
                className={cn(
                  'px-3 py-1.5 text-xs uppercase tracking-[0.14em] rounded-full border transition-colors',
                  collectionFilter === c
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground'
                )}
                data-testid={`admin-products-filter-${c}`}
              >
                {c} · {counts[c] || 0}
              </button>
            ))}
          </div>
          <div className="ml-auto relative flex-1 max-w-sm">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title or slug…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-foreground"
              data-testid="admin-products-search"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[80px_1fr_120px_100px_140px] gap-3 px-4 py-3 border-b border-border bg-secondary/30 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            <div>Image</div>
            <div>Product</div>
            <div>Collection</div>
            <div className="text-right">Price</div>
            <div className="text-right">Actions</div>
          </div>
          {loading && products.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No products match those filters.</div>
          ) : (
            visible.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-[80px_1fr_120px_100px_140px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center"
                data-testid={`admin-product-row-${p.id}`}
              >
                <div className="w-14 h-14 bg-muted rounded overflow-hidden flex items-center justify-center">
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                  <p className="text-xs text-muted-foreground truncate">/{p.slug}</p>
                  {p.paymentUrl && (
                    <p className="text-[10px] text-emerald-600 truncate mt-0.5">Custom payment link</p>
                  )}
                </div>
                <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{p.collection}</div>
                <div className="text-sm text-right tabular-nums">
                  {p.price === null || p.price === undefined ? (
                    <span className="italic text-muted-foreground">{p.priceLabel || 'Inquire'}</span>
                  ) : (
                    <span>${p.price}</span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    data-testid={`admin-product-edit-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(p)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`admin-product-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Drawer */}
      {drawerMode && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
          onClick={closeDrawer}
          data-testid="admin-products-drawer"
        >
          <div
            className="w-full max-w-xl bg-background border-l border-border overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl text-foreground">
                  {drawerMode === 'create' ? 'New product' : `Edit · ${draft.title}`}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {drawerMode === 'create'
                    ? 'ID and slug auto-generate if left blank.'
                    : `ID: ${editingId}`}
                </p>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
                data-testid="admin-products-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {formError}
                </div>
              )}

              <Field label="Title *">
                <input
                  value={draft.title}
                  onChange={(e) => setField('title', e.target.value)}
                  className={inputCls}
                  data-testid="admin-product-field-title"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Collection *">
                  <select
                    value={draft.collection}
                    onChange={(e) => setField('collection', e.target.value)}
                    className={inputCls}
                    data-testid="admin-product-field-collection"
                  >
                    {COLLECTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Badge (optional)">
                  <input
                    value={draft.badge}
                    onChange={(e) => setField('badge', e.target.value)}
                    placeholder="e.g. New, Limited"
                    className={inputCls}
                    data-testid="admin-product-field-badge"
                  />
                </Field>
              </div>

              <Field label="Description *">
                <textarea
                  value={draft.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={4}
                  className={cn(inputCls, 'resize-y')}
                  data-testid="admin-product-field-description"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Price (blank = Inquire only)">
                  <input
                    type="number"
                    value={draft.price}
                    onChange={(e) => setField('price', e.target.value)}
                    placeholder="e.g. 200"
                    className={inputCls}
                    data-testid="admin-product-field-price"
                  />
                </Field>
                <Field label="Price label (fallback)">
                  <input
                    value={draft.priceLabel}
                    onChange={(e) => setField('priceLabel', e.target.value)}
                    placeholder="Inquire"
                    className={inputCls}
                    data-testid="admin-product-field-priceLabel"
                  />
                </Field>
              </div>

              <Field label="Primary image URL *">
                <input
                  value={draft.image}
                  onChange={(e) => setField('image', e.target.value)}
                  placeholder="https://…"
                  className={inputCls}
                  data-testid="admin-product-field-image"
                />
                {draft.image && (
                  <img src={draft.image} alt="" className="mt-3 max-h-40 rounded border border-border" />
                )}
              </Field>

              <Field
                label="Additional views (JSON)"
                hint='Array of {"id","label","url"}. Use "interior" ids for book previews.'
              >
                <textarea
                  value={draft.imagesJson}
                  onChange={(e) => setField('imagesJson', e.target.value)}
                  rows={5}
                  placeholder='[{"id":"back","label":"Back","url":"https://..."}]'
                  className={cn(inputCls, 'resize-y font-mono text-xs')}
                  data-testid="admin-product-field-imagesJson"
                />
              </Field>

              <Field label="Custom Square payment URL (optional)">
                <input
                  value={draft.paymentUrl}
                  onChange={(e) => setField('paymentUrl', e.target.value)}
                  placeholder="https://square.link/u/…"
                  className={inputCls}
                  data-testid="admin-product-field-paymentUrl"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug (optional)">
                  <input
                    value={draft.slug}
                    onChange={(e) => setField('slug', e.target.value)}
                    placeholder="auto-generated"
                    className={inputCls}
                    data-testid="admin-product-field-slug"
                  />
                </Field>
                {drawerMode === 'create' && (
                  <Field label="Manual id (optional)">
                    <input
                      value={draft.id}
                      onChange={(e) => setField('id', e.target.value)}
                      placeholder="auto-generated"
                      className={inputCls}
                      data-testid="admin-product-field-id"
                    />
                  </Field>
                )}
                <Field label="Original category (optional)">
                  <input
                    value={draft.originalCategory}
                    onChange={(e) => setField('originalCategory', e.target.value)}
                    placeholder="e.g. limited-edition-prints"
                    className={inputCls}
                    data-testid="admin-product-field-originalCategory"
                  />
                </Field>
              </div>

              <label className="flex items-center gap-3 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.sold}
                  onChange={(e) => setField('sold', e.target.checked)}
                  className="w-4 h-4 accent-foreground"
                  data-testid="admin-product-field-sold"
                />
                <span className="text-sm text-foreground">
                  Mark as <strong>Sold</strong>
                </span>
                <span className="text-xs text-muted-foreground">
                  · Shown on the site with a "Sold" badge; not orderable
                </span>
              </label>
            </div>

            <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={closeDrawer} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="admin-products-save">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : drawerMode === 'create' ? 'Create product' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2 text-sm bg-card border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-foreground';

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </label>
);

export default AdminProductsPage;
